import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import { useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  EAppNameEnum,
  EFeatureFlagSlug,
  Permissions,
  hasFeatureFlag,
  hasPermissionInAccessMap,
} from '@onesource/schemas';
import { getSession, useGetAllProfileQuery, useGetProDirectoryPendingRevisionQuery } from '@onesource/services';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { FC, SyntheticEvent, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MdBookmark, MdPerson, MdSecurity } from 'react-icons/md';
import { CONFIG } from 'src/config-global';

import { PageContainer } from '#/components/page-container';

import { AccountBlockedUsersSection } from './account-blocked-users-section';
import { AccountPrivacySection } from './account-privacy-section';
import { ProDirectoryTab } from './account-pro-directory/tab';
import { ProfileTab } from './account-profile-tab';
import { SecurityTab } from './account-security-tab';

// ----------------------------------------------------------------------

const ALL_TAB_KEYS = ['profile', 'security', 'pro-directory'] as const;
type TTabKey = (typeof ALL_TAB_KEYS)[number];

export const AccountPage: FC = () => {
  const token = getSession();
  const { data } = useGetAllProfileQuery({ token });
  const {
    firstName,
    lastName,
    email,
    avatar,
    displayName,
    username,
    timezone,
    occupation,
    bio,
    primaryLanguage,
    phoneNumber,
    zoomEmail,
    address,
    socials,
    isPrivate,
    access,
    featureFlags,
  } = data || {};

  const { tab } = useSearch({ from: '/account' });
  const navigate = useNavigate({ from: '/account' });
  const theme = useTheme();
  const isStepperSlotViewport = useMediaQuery(theme.breakpoints.up('lg'));
  const [stepperSlotEl, setStepperSlotEl] = useState<HTMLDivElement | null>(null);
  const stepperSlot = isStepperSlotViewport ? stepperSlotEl : null;

  // Show the Pro Directory tab only when the user's access map grants the
  // `pro_directory.active` permission.
  const showProDirectory = hasPermissionInAccessMap(access, Permissions.PD_ACTIVE);
  const { data: pendingRevision } = useGetProDirectoryPendingRevisionQuery(showProDirectory);
  // The Privacy Mode block on the Security and Privacy tab is hidden by default;
  // grant the flag at dashboard /feature-flags to show it. The Profile tab always has it.
  const showSecurityPrivacyToggle = hasFeatureFlag(
    featureFlags,
    EFeatureFlagSlug.SHOW_SECURITY_PRIVACY_TOGGLE,
    EAppNameEnum.MEMBERS,
  );
  const tabKeys: TTabKey[] = showProDirectory ? ['profile', 'security', 'pro-directory'] : ['profile', 'security'];

  const requestedTab = (tab ?? 'profile') as TTabKey;
  const activeTabIndex = Math.max(0, tabKeys.indexOf(requestedTab));

  const handleTabChange = (_: SyntheticEvent, value: number) => {
    navigate({ search: { tab: tabKeys[value] }, replace: true });
  };

  return (
    <>
      <Helmet>
        <title> {`My Account - ${CONFIG.appName}`}</title>
      </Helmet>
      <PageContainer>
        <Container maxWidth="xl">
          <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
            My Account
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              borderBottom: 1,
              borderColor: 'divider',
              maxWidth: '100%',
            }}
          >
            <Tabs
              value={activeTabIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ flexShrink: 1, minWidth: 0, borderBottom: 0 }}
            >
              <Tab label="Profile" icon={<MdPerson size={20} />} iconPosition="start" />
              <Tab label="Security and Privacy" icon={<MdSecurity size={20} />} iconPosition="start" />
              {showProDirectory && (
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Pro Directory
                      {pendingRevision?.status === 'draft' && <Chip label="Draft" size="small" />}
                      {pendingRevision?.status === 'pending' && <Chip label="Pending" color="warning" size="small" />}
                    </Box>
                  }
                  icon={<MdBookmark size={20} />}
                  iconPosition="start"
                />
              )}
            </Tabs>

            {showProDirectory && isStepperSlotViewport && tabKeys[activeTabIndex] === 'pro-directory' && (
              <Box ref={setStepperSlotEl} sx={{ flex: 1, minWidth: 0 }} />
            )}
          </Box>
          <Box sx={{ height: { xs: 'calc(100dvh - 200px)', md: 'calc(100dvh - 234px)' }, overflowY: 'auto' }}>
            <Box sx={{ display: tabKeys[activeTabIndex] === 'profile' ? 'block' : 'none' }}>
              {data && (
                <ProfileTab
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  avatar={avatar}
                  displayName={displayName}
                  username={username}
                  timezone={timezone}
                  occupation={occupation}
                  bio={bio}
                  primaryLanguage={primaryLanguage}
                  phoneNumber={phoneNumber}
                  zoomEmail={zoomEmail}
                  address={address}
                  socials={socials}
                  isPrivate={isPrivate}
                />
              )}
            </Box>

            {showProDirectory && (
              <Box
                sx={{
                  display: tabKeys[activeTabIndex] === 'pro-directory' ? 'flex' : 'none',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <ProDirectoryTab
                  profileFullName={[firstName, lastName].filter(Boolean).join(' ') || displayName || ''}
                  profileBio={bio ?? ''}
                  profilePhone={phoneNumber ?? ''}
                  profileAvatar={avatar ?? ''}
                  profileSocials={{
                    facebook: socials?.facebook ?? '',
                    instagram: socials?.instagram ?? '',
                    youtube: socials?.youtube ?? '',
                    twitter: socials?.twitter ?? '',
                    linkedin: socials?.linkedin ?? '',
                    tiktok: socials?.tiktok ?? '',
                  }}
                  stepperSlot={stepperSlot}
                  isTabActive={tabKeys[activeTabIndex] === 'pro-directory'}
                />
              </Box>
            )}

            <Box sx={{ display: tabKeys[activeTabIndex] === 'security' ? 'block' : 'none', pb: 4 }}>
              <SecurityTab />
              {showSecurityPrivacyToggle && (
                <Box sx={{ mt: 6 }}>
                  <AccountPrivacySection />
                </Box>
              )}
              <Box sx={{ mt: 6 }}>
                <AccountBlockedUsersSection />
              </Box>
            </Box>
          </Box>
        </Container>
      </PageContainer>
    </>
  );
};
